import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';
import { errore, quandoBreve } from './comuni';

// Impostazioni → Attività (#estrattore-attivita, solo admin, GET /api/admin/estrattore/attivita): il diario
// dell'estrattore, l'unica parte del sistema che lavora da sola su un altro computer per ore. Quattro parti:
// cosa sta facendo adesso, la coda (nell'ordine vero, con «metti in cima», «salta» e «riprova»), lo storico di
// 24 ore o 7 giorni con l'esito a parole e cosa ha prodotto, e i contatori di ogni giorno.
//
// L'ancora #estrattore-attivita è quella che il popup dell'estensione apre con un link diretto
// (backend/diario_estrattore.py → riepilogo → `pagina`): non va cambiata.
//
// Si rilegge da sola ogni 30 secondi, ma solo se qualcuno sta guardando la pagina (document.hidden): interrogare
// il server per una scheda del browser chiusa in fondo è lavoro sprecato. I dati vecchi restano a video finché
// non arrivano i nuovi, così non sfarfalla niente e non si perde la riga che si stava leggendo.

export const OGNI = 30000;

// Gli esiti dello storico, con il colore: verde letto, rosso fallito, grigio saltato
const ESITI = { fatto: 't-g', fallito: 't-r', annullata: 't-m' };

const PERIODI = [[24, '24 ore'], [24 * 7, '7 giorni']];

// "2026-09-21" → "lun 21 set", senza passare da new Date("…") che la leggerebbe come mezzanotte UTC
export const giornoTesto = (iso) => {
  const pezzi = String(iso || '').split('-').map(Number);
  if (pezzi.length !== 3 || pezzi.some(Number.isNaN)) return iso || '—';
  const d = new Date(pezzi[0], pezzi[1] - 1, pezzi[2]);
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Dove porta una riga: la casa del cliente, oppure l'agenzia
export const dove = (r) => {
  if (r.cliente_id && r.immobile_id) return `/admin/clienti/${r.cliente_id}?evidenzia=${r.immobile_id}#immobili`;
  if (r.cliente_id) return `/admin/clienti/${r.cliente_id}`;
  if (r.agenzia_id) return `/admin/agenzie/${r.agenzia_id}`;
  return null;
};

// I pezzetti di una riga uniti da " · ", saltando quelli che non ci sono
const unite = (pezzi) => pezzi.filter(Boolean).map((p, i) => (
  // eslint-disable-next-line react/no-array-index-key
  <React.Fragment key={i}>{i ? ' · ' : ''}{p}</React.Fragment>
));

// Per chi è il lavoro: il cliente o l'agenzia, cliccabili
const perChi = (r) => {
  if (r.cliente_id && r.cliente) return <Link to={`/admin/clienti/${r.cliente_id}`}>{r.cliente}</Link>;
  if (r.agenzia_id && r.agenzia) return <Link to={`/admin/agenzie/${r.agenzia_id}`}>{r.agenzia}</Link>;
  return <span className="muto">{r.cliente || r.agenzia || '—'}</span>;
};

const AttivitaEstrattore = () => {
  const [dati, setDati] = useState(null);
  const [problema, setProblema] = useState(false);
  const [clienti, setClienti] = useState([]);
  const [ore, setOre] = useState(24);
  const [tipo, setTipo] = useState('');
  const [cliente, setCliente] = useState('');
  const [avviso, setAvviso] = useState('');
  const [attesa, setAttesa] = useState('');

  useEffect(() => { axios.get(`${API}/clienti-lista`).then((r) => setClienti(r.data.righe || [])).catch(() => {}); }, []);

  const carica = useCallback(async () => {
    try {
      const params = { ore, ...(tipo ? { tipo } : {}), ...(cliente ? { cliente } : {}) };
      const r = await axios.get(`${API}/admin/estrattore/attivita`, { params });
      setDati(r.data);
      setProblema(false);
    } catch {
      setProblema(true);
    }
  }, [ore, tipo, cliente]);

  useEffect(() => { carica(); }, [carica]);

  // ogni 30 secondi, e appena si torna a guardare la scheda del browser
  useEffect(() => {
    const battito = setInterval(() => { if (!document.hidden) carica(); }, OGNI);
    const tornato = () => { if (!document.hidden) carica(); };
    document.addEventListener('visibilitychange', tornato);
    return () => { clearInterval(battito); document.removeEventListener('visibilitychange', tornato); };
  }, [carica]);

  // Metti in cima / Salta / Riprova: dopo si rilegge tutto. Il 409 («non è più in coda») non è un errore nostro:
  // qualcun altro, o l'estrattore stesso, ha già fatto quel lavoro — si dice e si ricarica.
  const azione = async (r, quale, fatto) => {
    setAttesa(`${r.id}-${quale}`);
    try {
      await axios.post(`${API}/admin/estrattore/coda/${r.id}/${quale}`);
      setAvviso('');
      toast.success(fatto);
      await carica();
    } catch (e) {
      if (e?.response?.status === 409) {
        setAvviso(errore(e, 'Questo lavoro non è più in coda'));
        await carica();
      } else toast.error(errore(e, 'Non riuscito'));
    } finally { setAttesa(''); }
  };

  if (problema && !dati) {
    return (
      <div className="imp-griglia" data-testid="attivita-problema">
        <section className="card">
          <div className="ttl"><div className="i r"><Icona nome="avviso" /></div><h2>Attività dell&apos;estrattore</h2></div>
          <p className="imp-nota">Il diario dell&apos;estrattore non è arrivato dal server: riprovate fra poco.</p>
        </section>
      </div>
    );
  }
  if (!dati) return <p className="vuoto">Carico il diario dell&apos;estrattore…</p>;

  const a = dati.adesso || {};
  const coda = dati.coda || [];
  const storico = dati.storico || [];
  const contatori = dati.contatori || [];
  const filtrato = Boolean(tipo || cliente);

  const bottoni = (r, storia = false) => (
    <div className="azioni azioni-lavoro">
      {!storia && <button type="button" className="btn xs" disabled={attesa === `${r.id}-in-cima`} onClick={() => azione(r, 'in-cima', 'Messo in cima')}>Metti in cima</button>}
      {!storia && <button type="button" className="btn xs rosso-vuoto" disabled={attesa === `${r.id}-salta`} onClick={() => azione(r, 'salta', 'Saltato')}>Salta</button>}
      {(storia ? r.esito === 'fallito' : r.tentativi > 0) && (
        <button type="button" className="btn xs" disabled={attesa === `${r.id}-riprova`} onClick={() => azione(r, 'riprova', 'Rimesso in coda')}>Riprova</button>
      )}
    </div>
  );

  return (
    <div className="imp-griglia" data-testid="estrattore-attivita">
      {/* a) lo stato vivo */}
      <section className={`card estrattore attivita-ora${a.acceso ? '' : ' fermo'}`} data-testid="attivita-adesso">
        <div className="ttl"><div className={`i ${a.acceso ? 'g' : 'r'}`}><Icona nome="orologio" /></div><h2>Attività dell&apos;estrattore</h2>
          <span className={`tag ${a.acceso ? 't-g' : 't-r'}`} data-testid="attivita-acceso">{a.acceso ? 'acceso' : 'spento'}</span>
        </div>
        <div className="stato-est"><i /><b data-testid="attivita-sta-facendo">{a.sta_facendo || 'fermo'}</b></div>
        {/* «acceso, senza lavori da fare» non è vero se in coda ce ne sono trentacinque: la sera del 28/09/2026 la
            pagina lo diceva mentre alcuni aspettavano da tredici giorni, perché le pagine rimaste su quel portale
            erano tutte riservate agli annunci dei clienti. Adesso si dice quale portale, quanti lavori, e perché. */}
        {(a.fermi_perche || []).length > 0 && (
          <div className="doppione" data-testid="attivita-fermi-perche">
            {a.fermi_perche.map((f) => (
              <p key={f.portale} style={{ margin: '.15rem 0' }}>
                <b>{f.portale}.it</b>: {f.quanti === 1 ? '1 lavoro aspetta' : `${f.quanti} lavori aspettano`} — {f.perche}
              </p>
            ))}
          </div>
        )}
        <p className="imp-nota" data-testid="attivita-battito">
          {a.dispositivo || 'dispositivo sconosciuto'}{a.versione ? ` · versione ${a.versione}` : ''} · ultimo battito {a.da_quanto || 'mai'}
          {a.messaggio ? ` · ${a.messaggio}` : ''}
        </p>
        <div className="kv" style={{ marginTop: '.8rem' }}>
          <div><small>Pagine aperte oggi</small><b data-testid="attivita-pagine">{a.pagine_oggi || 0}{a.limite ? ` / ${a.limite}` : ''}</b></div>
          <div><small>Annunci letti oggi</small><b>{a.annunci_oggi || 0}</b></div>
          <div><small>Captcha oggi</small><b className={a.captcha_oggi ? 'no' : 'g'} data-testid="attivita-captcha">{a.captcha_oggi || 0}</b></div>
          {/* «in coda» vuol dire in coda: i lavori già presi (in corso) stanno nell'elenco qui sotto, in cima e
              segnati, ma non in questo numero — se no il riquadro «Estrattore» e il popup ne dicevano due diversi
              con lo stesso nome (blocco 5, secondo giro). */}
          <div><small>Lavori in coda</small><b data-testid="attivita-in-coda">
            {coda.filter((v) => v.stato === 'in_coda').length}{filtrato ? ' (con i filtri)' : ''}</b></div>
        </div>
        {(a.portali_fermi || []).length > 0 && (
          <p className="avviso" data-testid="attivita-portali-fermi">
            <b>Portali fermi:</b> {a.portali_fermi.map((p) => (typeof p === 'string' ? p : `${p.portale}${p.motivo ? ` (${p.motivo})` : ''}`)).join(' · ')}
          </p>
        )}
        {/* Quanto resta oggi su ogni portale, e chi è fermo e perché (backend/portali.py): il limite del giorno
            finito, la pausa a mano, l'«accesso temporaneamente limitato» che il portale stesso ci ha detto.
            I limiti si cambiano in Impostazioni → Estrattore → Portali. */}
        {(dati.portali?.portali || []).length > 0 && (
          <p className="imp-nota" data-testid="attivita-portali">
            {dati.portali.portali.map((p) => `${p.nome}: ${p.fermo ? p.testo.replace(`${p.nome}: `, '') : `${p.oggi}/${p.limite}`}`).join(' · ')}
          </p>
        )}
        {a.prossima_pausa && <p className="imp-nota" data-testid="attivita-pausa">Prossima pausa: {a.prossima_pausa}</p>}
      </section>

      {/* b) la coda */}
      <section className="card" data-testid="attivita-coda">
        <div className="ttl"><div className="i b"><Icona nome="immobili" /></div><h2>In coda <small>{coda.length}</small></h2></div>
        <div className="riga-filtri">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} aria-label="Tipo di lavoro">
            <option value="">Tutti i tipi</option>
            {(dati.tipi || []).map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
          <select value={cliente} onChange={(e) => setCliente(e.target.value)} aria-label="Cliente">
            <option value="">Tutti i clienti</option>
            {clienti.map((c) => <option key={c.id} value={c.id}>{c.nome_intero || c.nome}</option>)}
          </select>
          {filtrato && <button type="button" className="link" onClick={() => { setTipo(''); setCliente(''); }}>Tutto</button>}
        </div>
        {avviso && <p className="avviso avviso-lavoro" data-testid="attivita-avviso">{avviso}</p>}
        {!coda.length && <p className="vuoto">{filtrato ? 'Nessun lavoro in coda con questi filtri.' : 'Niente in coda: l\'estrattore ha finito il lavoro.'}</p>}
        {coda.length > 0 && (
          <div className="imp-righe lavori">
            {coda.map((r) => (
              <div key={r.id} data-testid={`coda-${r.id}`} className={r.stato === 'in_corso' ? 'in-corso' : undefined}>
                {/* «cliente» o «staff»: è una casa che qualcuno ha messo in lista, e sta aspettando. Gli annunci
                    vengono prima di tutto il resto (backend/priorita_coda.py) e qui stanno sempre in cima; chi li
                    ha inseriti si legge dall'etichetta, perché i clienti vengono prima dello staff. */}
                {r.di_chi === 'cliente' && <span className="tag t-g" data-testid={`coda-cliente-${r.id}`}>cliente</span>}
                {r.di_chi === 'staff' && <span className="tag t-b" data-testid={`coda-staff-${r.id}`}>staff</span>}
                <span className="tag t-o">{r.tipo_testo}</span>
                {/* quale passo della catena, per i lavori sulle agenzie: due passi diversi sembravano lo stesso
                    lavoro fatto due volte */}
                {r.passo && <span className="tag t-b" data-testid={`coda-passo-${r.id}`}>{r.passo}</span>}
                {r.stato === 'in_corso' && <span className="tag t-b">in corso</span>}
                {r.troppo_vecchio && (
                  <span className="tag t-r" data-testid={`coda-vecchio-${r.id}`}>aspetta {r.aspetta_da}</span>
                )}
                {r.in_attesa && <span className="tag t-w">fermo su un captcha</span>}
                <b className="cosa">{dove(r) ? <Link to={dove(r)}>{r.cosa}</Link> : r.cosa}</b>
                <span className="descr">{unite([
                  // se il lavoro è «Ermes Immobiliare» per l'agenzia Ermes, non lo si ripete due volte
                  (r.cliente || r.agenzia) && r.cosa !== (r.cliente || r.agenzia) ? <>per {perChi(r)}</> : null,
                  r.portale,
                  r.in_coda_da && `aspetta ${r.in_coda_da}`,
                  r.tentativi > 0 && `${r.tentativi} ${r.tentativi === 1 ? 'tentativo' : 'tentativi'}`,
                  r.link && <a href={r.link} target="_blank" rel="noopener noreferrer">annuncio ↗</a>,
                ])}</span>
                {bottoni(r)}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* c) lo storico */}
      <section className="card" data-testid="attivita-storico">
        <div className="ttl"><div className="i"><Icona nome="fatto" /></div><h2>Cosa ha fatto <small>{storico.length}</small></h2>
          <div className="imp-azioni" role="group" aria-label="Periodo dello storico">
            {PERIODI.map(([v, testo]) => (
              <button key={v} type="button" className={`chip${ore === v ? ' on' : ''}`} aria-pressed={ore === v} onClick={() => setOre(v)}>{testo}</button>
            ))}
          </div>
        </div>
        {!storico.length && <p className="vuoto">Nessun lavoro finito {ore === 24 ? 'nelle ultime 24 ore' : 'negli ultimi 7 giorni'}.</p>}
        {storico.length > 0 && (
          <div className="tabella-box">
            <table className="tabella storico-estrattore">
              <thead><tr><th>Ora</th><th>Durata</th><th>Cosa</th><th>Esito</th><th>Cosa ha prodotto</th><th> </th></tr></thead>
              <tbody>
                {storico.map((r) => (
                  <tr key={r.id} data-testid={`storico-${r.id}`}>
                    <td className="nowrap muto">{r.ora || '—'}</td>
                    <td className="nowrap muto">{r.durata || '—'}</td>
                    <td>{dove(r) ? <Link to={dove(r)}>{r.cosa}</Link> : r.cosa}
                      <small>{unite([r.tipo_testo, r.cosa !== (r.cliente || r.agenzia) && (r.cliente || r.agenzia), r.portale])}</small></td>
                    <td className="nowrap"><span className={`tag ${ESITI[r.esito] || 't-m'}`}>{r.esito_testo || r.esito}</span>
                      {r.captcha && <small>captcha</small>}</td>
                    <td>{r.prodotto || '—'}</td>
                    <td className="nowrap">{r.esito === 'fallito' ? bottoni(r, true) : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* d) i contatori per giorno */}
      <section className="card" data-testid="attivita-contatori">
        <div className="ttl"><div className="i"><Icona nome="torta" /></div><h2>Giorno per giorno</h2></div>
        {!contatori.length && <p className="vuoto">Ancora nessun giorno da contare.</p>}
        {contatori.length > 0 && (
          <div className="tabella-box">
            <table className="tabella contatori-estrattore">
              <thead>
                <tr><th>Giorno</th><th>Annunci letti</th><th>Ricerche</th><th>Annunci nuovi</th><th>Aste escluse</th><th>Doppioni uniti</th>
                  <th>Agenzie completate</th><th>Ricontrolli</th><th>Prezzi cambiati</th><th>Ritirati</th><th>Falliti</th></tr>
              </thead>
              <tbody>
                {contatori.map((g) => (
                  <tr key={g.giorno} data-testid={`contatori-${g.giorno}`}>
                    <td className="nowrap">{giornoTesto(g.giorno)}</td>
                    <td>{g.annunci_letti || 0}</td><td>{g.ricerche || 0}</td><td>{g.annunci_nuovi || 0}</td>
                    <td>{g.aste_escluse || 0}</td><td>{g.doppioni_uniti || 0}</td><td>{g.agenzie_completate || 0}</td>
                    <td>{g.ricontrolli || 0}</td><td>{g.prezzi_cambiati || 0}</td><td>{g.ritirati || 0}</td>
                    <td className={g.falliti ? 'no' : undefined}>{g.falliti || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="imp-nota" style={{ marginTop: '.6rem' }} data-testid="attivita-letto">
          Questa pagina si rilegge da sola ogni 30 secondi, finché la tenete aperta davanti.
          {dati.letto_il ? ` Ultima lettura: ${quandoBreve(dati.letto_il)}.` : ''}
        </p>
      </section>
    </div>
  );
};

export default AttivitaEstrattore;
